/**
 * ملف الطالب — `/students/{id}`
 * (design/StudentRecord.dc.html + design/StudentLeaves.dc.html).
 *
 * ست تبويبات زي التصميم، وكل تبويب بيعرض **اللي السيستم بيرجّعه فعلًا**:
 *   الأساسيات · الدراسة والتقييم · التواصل والمشكلات · الحضور والإجازات
 *   · الحسابات · الإدارة
 *
 * 🔴 أهم حاجة في الشاشة دي هي **الاستكمال في مكانه**: الحقل الناقص بيتملي
 *    من هنا (`PATCH /students/{id}` أو `PATCH /families/{id}`) والوسم بيختفي
 *    فورًا — عشان الفريق ينضّف الداتا وهو شغّال بدل ما يرجع للشيت.
 *
 * ⛔ قواعد محروسة هنا:
 *   • الوسم بيختفي لما **السيستم** يقول إنه اتحل (الرد بيرجّع
 *     `data_quality` جديدة) — مش لما المستخدم يكتب في الخانة.
 *   • التعديل بلا `If-Match` ممنوع (الباك إند بيرمي 428). النسخة بتتاخد من
 *     السجل المقروء، ولو حد تاني سبقنا (409) بنعيد التحميل بدل ما ندهس شغله.
 *   • «الاشتراكات/الإجازات/سجل التواصل محجوبة» **مش** «مفيش» — الرد فيه
 *     `*_visible` والشاشة بتفرّق.
 *   • ⛔ ممنوع نخمّن منطقة زمنية من الدولة (كندا فيها ٦) — الخانة بتتكتب
 *     بالإيد أو بتفضل فاضية.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import { Icon } from "@/components/Icon";
import { ErrorState, LoadingState, OfflineState } from "@/components/states/ScreenStates";
import {
  Avatar,
  Dash,
  DateAr,
  DateRangeAr,
  DefRow,
  HiddenSection,
  Maybe,
  NeedsReviewMark,
  Section,
  VipMark,
} from "@/components/RegistryBits";
import { Ltr, fmtDate, fmtNum } from "@/lib/format";
import { ApiError, errorMessageAr, OfflineError } from "@/api/client";
import { Link } from "@/lib/router";
import { useSession } from "@/lib/session";
import {
  CONTACT_CHANNEL_OPTIONS,
  LANGUAGE_OPTIONS,
  MANUAL_STUDENT_STATUSES,
  STATUS_NOT_MANUAL_AR,
  UNFIXABLE_HERE_AR,
  channelAr,
  directionAr,
  fetchFamily,
  fetchStudent,
  fixesFor,
  COUNTRY_OPTIONS,
  hoursPolicyAr,
  languageAr,
  latinDigits,
  leaveSource,
  leaveTone,
  logFamilyContact,
  patchFamily,
  patchStudent,
  setFamilyTag,
  setStudentStatus,
  studentTone,
  type DataQuality,
  type EnrollmentRow,
  type FamilyDetail,
  type FixField,
  type SiblingRef,
  type StudentDetail,
} from "@/api/registry";
import "@/styles/registry.css";

const TABS = [
  { key: "basics", label: "الأساسيات" },
  { key: "study", label: "الدراسة والتقييم" },
  { key: "contact", label: "التواصل والمشكلات" },
  { key: "leaves", label: "الحضور والإجازات" },
  { key: "accounts", label: "الحسابات" },
  { key: "admin", label: "الإدارة" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; student: StudentDetail }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

/** الأسرة بتتحمّل لوحدها: ممكن تكون محجوبة عن القسم من غير ما الطالب يتحجب */
type FamilyState =
  | { kind: "none" }
  | { kind: "loading" }
  | { kind: "ready"; family: FamilyDetail }
  | { kind: "blocked"; messageAr: string };

export function StudentRecord({ studentId }: { studentId: string }) {
  const { me } = useSession();
  const perms = useMemo(() => new Set(me?.permissions ?? []), [me]);

  const [tab, setTab] = useState<TabKey>("basics");
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [family, setFamily] = useState<FamilyState>({ kind: "none" });
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  const showToast = useCallback((text: string) => {
    setToast(text);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const student = await fetchStudent(studentId);
      setState({ kind: "ready", student });

      const familyId = student.family?.id ?? null;
      if (!familyId) {
        setFamily({ kind: "none" });
        return;
      }
      setFamily({ kind: "loading" });
      try {
        setFamily({ kind: "ready", family: await fetchFamily(familyId) });
      } catch (err) {
        // ⛔ مش فشل صامت: نطاق الأسر أضيق من نطاق الطلاب، والشاشة بتقول السبب
        console.error(`[student] بيانات الأسرة ${familyId} مش متاحة`, err);
        setFamily({ kind: "blocked", messageAr: errorMessageAr(err) });
      }
    } catch (err) {
      console.error(`[student] تحميل سجل الطالب ${studentId} فشل`, err);
      setState(
        err instanceof OfflineError
          ? { kind: "offline" }
          : { kind: "error", messageAr: errorMessageAr(err) },
      );
    }
  }, [studentId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") {
    return (
      <Shell title="سجل الطالب">
        <LoadingState rows={5} />
      </Shell>
    );
  }
  if (state.kind === "offline") {
    return (
      <Shell title="سجل الطالب">
        <OfflineState onRetry={() => void load()} />
      </Shell>
    );
  }
  if (state.kind === "error") {
    return (
      <Shell title="سجل الطالب">
        <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
        <Link to="/students" className="link">
          ‹ رجوع لسجل الطلاب
        </Link>
      </Shell>
    );
  }

  const s = state.student;
  const fam = family.kind === "ready" ? family.family : null;
  const canFixStudent = perms.has("student.write");
  const canFixFamily = perms.has("family.write");
  const openIssues =
    (s.data_quality.needs_review ? s.data_quality.issues.length : 0) +
    (fam?.data_quality.needs_review ? fam.data_quality.issues.length : 0);

  return (
    <Shell
      title="سجل الطالب"
      crumb={
        <>
          {s.student_code ? <Ltr>{s.student_code}</Ltr> : "بلا كود"}
          {s.family ? ` · ${s.family.name_ar}` : ""}
        </>
      }
    >
      {/* ═══ (1) شريط الرجوع ═══ */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Link to="/students" className="link">
          ‹ رجوع لسجل الطلاب
        </Link>
        <button type="button" className="link" onClick={() => void load()}>
          تحديث السجل
        </button>
      </div>

      {/* ═══ (2) هيدر الطالب ═══ */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div className="accent-bar" />
        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Avatar name={s.name_ar} size={48} />
            <div
              className="clip"
              style={{ fontSize: 20, fontWeight: 600, color: "var(--blue-900)" }}
            >
              {s.name_ar || <span className="missing">بلا اسم مسجّل</span>}
            </div>
            {s.age_years === null ? (
              <span className="chip ghost">السن مش مسجّل</span>
            ) : (
              <span className="chip ghost">
                <span className="num">{fmtNum(s.age_years)}</span> سنة
              </span>
            )}
            {s.family?.country ? (
              <span className="chip ghost">
                <Ltr>{s.family.country}</Ltr>
              </span>
            ) : null}
            {languageAr(s.family?.primary_language) ? (
              <span className="chip ghost">{languageAr(s.family?.primary_language)}</span>
            ) : null}
            {s.status_label_ar ? (
              <span className={`chip ${studentTone(s.status)}`}>
                <span className="bullet" />
                {latinDigits(s.status_label_ar)}
              </span>
            ) : null}
            {/* تصنيف الأسرة — بييجي مع صف الطالب نفسه بلا نداء زيادة */}
            {s.family?.is_vip ? <VipMark tagsAr={[{ key: "vip", label_ar: "VIP" }]} /> : null}
            {openIssues > 0 ? (
              <button
                type="button"
                className="btn sm ghost"
                style={{ marginRight: "auto" }}
                onClick={() => setTab("basics")}
              >
                استكمال <span className="num">{fmtNum(openIssues)}</span> ناقص
              </button>
            ) : null}
          </div>
          <div className="t-desc">
            {s.owner ? `المسؤولة ${s.owner.name_ar}` : "مفيش مسؤولة متسندة"}
            {" · "}
            {s.family ? `أسرة ${s.family.name_ar}` : "مش مربوط بأسرة"}
            {s.status_reason_ar ? ` · ${latinDigits(s.status_reason_ar)}` : ""}
          </div>
        </div>
      </div>

      {/* ═══ (3) التبويبات ═══ */}
      <div className="reg-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`filt${tab === t.key ? " on" : ""}`}
            aria-pressed={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === "basics" && openIssues > 0 ? (
              <>
                {" "}
                <span className="num">{fmtNum(openIssues)}</span>
              </>
            ) : null}
            {t.key === "contact" && s.contact_log.length > 0 ? (
              <>
                {" "}
                <span className="num">{fmtNum(s.contact_log.length)}</span>
              </>
            ) : null}
            {t.key === "leaves" && s.leaves.length > 0 ? (
              <>
                {" "}
                <span className="num">{fmtNum(s.leaves.length)}</span>
              </>
            ) : null}
          </button>
        ))}
      </div>

      {/* ═══ (4) العمودان ═══ */}
      <div className="reg-cols">
        <div className="reg-col">
          {tab === "basics" ? (
            <BasicsTab
              student={s}
              family={family}
              canFixStudent={canFixStudent}
              canFixFamily={canFixFamily}
              onSaved={(msg) => {
                showToast(msg);
                void load();
              }}
            />
          ) : null}
          {tab === "study" ? <StudyTab student={s} /> : null}
          {tab === "contact" ? (
            <ContactTab
              student={s}
              canWrite={canFixFamily}
              onSaved={(msg) => {
                showToast(msg);
                void load();
              }}
            />
          ) : null}
          {tab === "leaves" ? <LeavesTab student={s} /> : null}
          {tab === "accounts" ? <AccountsTab student={s} /> : null}
          {tab === "admin" ? (
            <AdminTab
              student={s}
              family={fam}
              canChange={canFixStudent}
              canElevate={perms.has("student.archive")}
              canTagFamily={canFixFamily}
              onOpenLeaves={() => setTab("leaves")}
              onSaved={(msg) => {
                showToast(msg);
                void load();
              }}
            />
          ) : null}
        </div>

        <div className="reg-col">
          {tab === "leaves" ? <CourseBalanceCard enrollments={s.enrollments} visible={s.enrollments_visible} /> : null}
          <FamilyCard state={family} siblings={s.siblings ?? []} />
          <QualityCard title="جودة سجل الطالب" q={s.data_quality} />
        </div>
      </div>

      {toast ? (
        <div
          style={{ position: "fixed", bottom: 24, right: "50%", transform: "translateX(50%)", zIndex: 40 }}
        >
          <div className="toast" role="status">
            <Icon name="check" size={14} stroke="#FFFFFF" strokeWidth={2.2} />
            {toast}
          </div>
        </div>
      ) : null}
    </Shell>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   (أ) الأساسيات — ومعاها 🔴 الاستكمال في مكانه
   ═══════════════════════════════════════════════════════════════════ */

function BasicsTab({
  student,
  family,
  canFixStudent,
  canFixFamily,
  onSaved,
}: {
  student: StudentDetail;
  family: FamilyState;
  canFixStudent: boolean;
  canFixFamily: boolean;
  onSaved: (toastAr: string) => void;
}) {
  const fam = family.kind === "ready" ? family.family : null;
  return (
    <>
      <Section title="بيانات الطالب">
        <div className="deflist">
          <DefRow k="الاسم بالعربي">
            <Maybe v={student.name_ar} />
          </DefRow>
          <DefRow k="الاسم بالإنجليزي">
            {student.name_en ? <Ltr>{student.name_en}</Ltr> : <Dash />}
          </DefRow>
          <DefRow k="الكود">
            {student.student_code ? <Ltr>{student.student_code}</Ltr> : <Dash />}
          </DefRow>
          <DefRow k="تاريخ الميلاد">
            <DateAr v={student.birth_date} />
          </DefRow>
          <DefRow k="السن">
            {student.age_years === null ? (
              <Dash />
            ) : (
              <>
                <span className="num">{fmtNum(student.age_years)}</span> سنة
              </>
            )}
          </DefRow>
          <DefRow k="النوع">
            {student.gender === "m" ? "ولد" : student.gender === "f" ? "بنت" : <Dash />}
          </DefRow>
          <DefRow k="آخر تواصل">
            <DateAr v={student.last_contact_at} />
          </DefRow>
        </div>
      </Section>

      <FixSection
        student={student}
        family={family}
        canFixStudent={canFixStudent}
        canFixFamily={canFixFamily}
        onSaved={onSaved}
      />

      {family.kind === "blocked" ? (
        <Section title="بيانات الأسرة">
          <div className="t-desc">بيانات الأسرة مش متاحة لك — {family.messageAr}</div>
        </Section>
      ) : null}
      {fam ? (
        <Section
          title="بيانات الأسرة"
          note={
            fam.children_count === null ? undefined : (
              <>
                <span className="num">{fmtNum(fam.children_count)}</span> طالب في الأسرة
              </>
            )
          }
        >
          <div className="deflist">
            <DefRow k="اسم الأسرة">
              <Maybe v={fam.name_ar} />
            </DefRow>
            <DefRow k="ولي الأمر">
              <Maybe v={fam.guardian.name_ar} />
            </DefRow>
            <DefRow k="رقم التواصل">
              {!fam.guardian.phone_visible ? (
                // ⛔ مش «مفيش رقم» — القسم ده مالوش حق يشوف الرقم أصلًا
                <span className="missing">الرقم محجوب عن قسمك</span>
              ) : fam.guardian.phone_masked ? (
                <Ltr>{fam.guardian.phone_masked}</Ltr>
              ) : (
                <span className="missing">مفيش رقم مسجّل</span>
              )}
            </DefRow>
            <DefRow k="الدولة">
              {fam.country ? (
                <Ltr>{fam.country}</Ltr>
              ) : fam.country_raw ? (
                <>
                  <span className="missing">مش متعرّفة — </span>
                  <span className="raw-quote">{fam.country_raw}</span>
                </>
              ) : (
                <Dash />
              )}
            </DefRow>
            <DefRow k="المنطقة الزمنية">
              {fam.timezone ? <Ltr>{fam.timezone}</Ltr> : <Dash />}
            </DefRow>
            <DefRow k="اللغة">
              {fam.primary_language_ar ?? languageAr(fam.primary_language) ?? <Dash />}
            </DefRow>
            <DefRow k="الحالة">
              <Maybe v={fam.status_label_ar} />
            </DefRow>
            <DefRow k="تصنيف">
              {fam.tags_ar.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {fam.is_vip ? <VipMark tagsAr={fam.tags_ar} /> : null}
                  {fam.tags_ar
                    .filter((t) => t.key === "referral")
                    .map((t) => (
                      <span key={t.key} className="chip ghost">
                        {t.label_ar}
                      </span>
                    ))}
                </div>
              ) : (
                <Dash />
              )}
            </DefRow>
          </div>
        </Section>
      ) : null}
    </>
  );
}

/**
 * 🔴 كارت الاستكمال — كل وسم نقص وجنبه الخانة اللي بتحلّه.
 *
 * ⛔ **فعل أساسي واحد بس**: زرار «حفظ الاستكمال» واحد في آخر الكارت، مش
 *    زرار لكل خانة. غير إن ده قاعدة التصميم (زرار كحلي واحد في الشاشة)،
 *    هو كمان أقل ضغطات: الموظفة بتملا اللي قدامها وتحفظ مرة واحدة.
 *
 * ⛔ والوسم اللي مالوش خانة (زي «الخانة فيها أكتر من شخص») بيتعرض بسببه
 *    ومكتوب جنبه ليه مش بيتحل من هنا — ما بيتشالش من العرض عشان ما يبانش
 *    إننا نسيناه.
 *
 * ⚠️ الحفظ ممكن يكون على موردين (الطالب وأسرته) — دول كتبتين منفصلتين لأن
 *    لكل واحد مساره ونسخته. لو واحدة نجحت والتانية فشلت، **الاتنين بيتقالوا**
 *    مش بس الأولى.
 */
type FixItem = {
  key: string;
  target: "student" | "family";
  issue: string;
  labelAr: string;
  fields: FixField[];
  canWrite: boolean;
};

function FixSection({
  student,
  family,
  canFixStudent,
  canFixFamily,
  onSaved,
}: {
  student: StudentDetail;
  family: FamilyState;
  canFixStudent: boolean;
  canFixFamily: boolean;
  onSaved: (toastAr: string) => void;
}) {
  const fam = family.kind === "ready" ? family.family : null;

  /** القيم المكتوبة — المفتاح `student.name_ar` / `family.timezone` */
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const items: FixItem[] = [];
  if (student.data_quality.needs_review) {
    student.data_quality.issues.forEach((issue, i) => {
      items.push({
        key: `student:${issue}`,
        target: "student",
        issue,
        labelAr: student.data_quality.issues_ar[i] ?? issue,
        fields: fixesFor("student", issue),
        canWrite: canFixStudent,
      });
    });
  }
  if (fam?.data_quality.needs_review) {
    fam.data_quality.issues.forEach((issue, i) => {
      items.push({
        key: `family:${issue}`,
        target: "family",
        issue,
        labelAr: fam.data_quality.issues_ar[i] ?? issue,
        fields: fixesFor("family", issue),
        canWrite: canFixFamily,
      });
    });
  }

  if (items.length === 0) {
    return (
      <Section title="استكمال الناقص">
        <div className="t-desc">
          <span className="chip okc">
            <span className="bullet" />
            السجل مكتمل
          </span>{" "}
          مفيش خانة ناقصة في سجل الطالب ولا في أسرته.
        </div>
      </Section>
    );
  }

  const payloads = buildPayloads(values);
  const filledCount =
    Object.keys(payloads.student).length + Object.keys(payloads.family).length;

  async function save() {
    if (busy || filledCount === 0) return;
    setBusy(true);
    setErrors([]);
    const problems: string[] = [];
    const toasts: string[] = [];

    if (Object.keys(payloads.student).length > 0) {
      try {
        // ⚠️ `student.version` هي النسخة اللي قرينا بيها — الباك إند بيرفض
        //    الكتابة لو حد تاني سبقنا (409) بدل ما يدهس شغله بصمت.
        const out = await patchStudent(student.id, student.version, payloads.student);
        toasts.push(out.toastAr);
      } catch (err) {
        console.error("[student] استكمال سجل الطالب فشل", err);
        problems.push(`سجل الطالب: ${errorMessageAr(err)}`);
      }
    }
    if (Object.keys(payloads.family).length > 0) {
      if (!fam) {
        problems.push("سجل الأسرة: مش محمّل — افتح الصفحة تاني.");
      } else {
        try {
          const out = await patchFamily(fam.id, fam.version, payloads.family);
          toasts.push(out.toastAr);
        } catch (err) {
          console.error("[student] استكمال سجل الأسرة فشل", err);
          problems.push(`سجل الأسرة: ${errorMessageAr(err)}`);
        }
      }
    }

    setBusy(false);
    setErrors(problems);
    if (toasts.length > 0) {
      // الخانات اللي نجحت بتتفضّى، واللي فشلت بتفضل مكتوبة عشان ما تتكتبش تاني
      setValues((cur) => dropSaved(cur, problems.length === 0 ? null : problems));
      onSaved(toasts.join(" · "));
    }
  }

  return (
    <Section
      title="استكمال الناقص"
      note="املا الخانة هنا — الوسم بيختفي لما السيستم يتأكد إن النقص اتحل فعلًا"
    >
      {items.map((item) => (
        <FixRow
          key={item.key}
          item={item}
          values={values}
          onChange={(name, v) =>
            setValues((cur) => ({ ...cur, [`${item.target}.${name}`]: v }))
          }
          rawSheet={
            (item.target === "student" ? student.data_quality.raw : fam?.data_quality.raw) ?? null
          }
        />
      ))}

      <div
        className="fix-line"
        style={{ borderTop: "1px solid var(--line)", paddingTop: 12 }}
      >
        <span className="t-meta">
          {filledCount === 0 ? (
            "املا خانة واحدة على الأقل"
          ) : (
            <>
              <span className="num">{fmtNum(filledCount)}</span> خانة جاهزة للحفظ
            </>
          )}
        </span>
        {/* ⛔ الفعل الأساسي الوحيد في الشاشة */}
        <button
          type="button"
          className={`btn sm ${filledCount > 0 ? "primary" : "is-off"}${busy ? " is-busy" : ""}`}
          style={{ marginRight: "auto" }}
          disabled={busy || filledCount === 0}
          onClick={() => void save()}
        >
          {busy ? "بيتحفظ…" : "حفظ الاستكمال"}
        </button>
      </div>

      {errors.map((e, i) => (
        <div className="t-meta" style={{ color: "var(--danger)" }} role="alert" key={i}>
          {e}
        </div>
      ))}
    </Section>
  );
}

/** بيفصل القيم المكتوبة على موردَيْها، وبيشيل الفاضي */
function buildPayloads(values: Record<string, string>): {
  student: Record<string, string>;
  family: Record<string, string>;
} {
  const out = { student: {} as Record<string, string>, family: {} as Record<string, string> };
  for (const [key, raw] of Object.entries(values)) {
    const value = raw.trim();
    if (!value) continue;
    const dot = key.indexOf(".");
    if (dot === -1) continue;
    const target = key.slice(0, dot);
    const name = key.slice(dot + 1);
    if (target === "student") out.student[name] = value;
    else if (target === "family") out.family[name] = value;
  }
  return out;
}

/** بعد الحفظ الناجح الخانات بتتفضّى — إلا لو فيه مورد فشل، فقيمه بتفضل */
function dropSaved(
  values: Record<string, string>,
  problems: string[] | null,
): Record<string, string> {
  if (problems === null) return {};
  const keepStudent = problems.some((p) => p.startsWith("سجل الطالب"));
  const keepFamily = problems.some((p) => p.startsWith("سجل الأسرة"));
  const next: Record<string, string> = {};
  for (const [key, v] of Object.entries(values)) {
    if (key.startsWith("student.") && keepStudent) next[key] = v;
    if (key.startsWith("family.") && keepFamily) next[key] = v;
  }
  return next;
}

function FixRow({
  item,
  values,
  onChange,
  rawSheet,
}: {
  item: FixItem;
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  rawSheet: Record<string, string> | null;
}) {
  const unfixable = item.fields.length === 0;
  const why = UNFIXABLE_HERE_AR[item.issue] ?? "الوسم ده مالوش خانة في الشاشة دي.";

  return (
    <div className="fix">
      <div className="fix-line">
        <span className="src">{item.labelAr}</span>
        {unfixable ? <span className="t-meta">{why}</span> : null}
        {!unfixable && !item.canWrite ? (
          <span className="t-meta">مالكش صلاحية تعديل السجل ده</span>
        ) : null}
      </div>

      {rawSheet && Object.keys(rawSheet).length > 0 ? (
        <div className="raw-quote">
          نص الشيت:{" "}
          {Object.entries(rawSheet)
            .map(([k, v]) => `${k}: ${v}`)
            .join(" · ")}
        </div>
      ) : null}

      {!unfixable && item.canWrite
        ? item.fields.map((f) => {
            const key = `${item.target}.${f.name}`;
            return (
              <div className="fix-line" key={f.name}>
                <span className="t-meta" style={{ width: 108, flexShrink: 0 }}>
                  {f.label_ar}
                </span>
                {f.kind === "language" ? (
                  <select
                    className="reg-select"
                    aria-label={f.label_ar}
                    value={values[key] ?? ""}
                    onChange={(e) => onChange(f.name, e.target.value)}
                  >
                    {/* ⛔ مفيش لغة افتراضية — الاختيار الفاضي بيفضل فاضي */}
                    <option value="">اختار اللغة…</option>
                    {LANGUAGE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label_ar}
                      </option>
                    ))}
                  </select>
                ) : f.kind === "country" ? (
                  <select
                    className="reg-select"
                    aria-label={f.label_ar}
                    value={values[key] ?? ""}
                    onChange={(e) => onChange(f.name, e.target.value)}
                  >
                    {/* ⛔ مفيش دولة افتراضية — الاختيار الفاضي بيفضل فاضي */}
                    <option value="">اختار الدولة…</option>
                    {COUNTRY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label_ar}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="reg-input"
                    type="text"
                    // الاسم عربي ⇒ auto · المنطقة الزمنية والدولة والرقم لاتيني صافي
                    dir={f.kind === "text" ? "auto" : "ltr"}
                    aria-label={f.label_ar}
                    value={values[key] ?? ""}
                    placeholder={f.hint_ar || undefined}
                    onChange={(e) => onChange(f.name, e.target.value)}
                  />
                )}
                {f.hint_ar ? <span className="t-meta">{f.hint_ar}</span> : null}
              </div>
            );
          })
        : null}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   (ب) الدراسة والتقييم
   ═══════════════════════════════════════════════════════════════════ */

/** نتيجة حصة التقييم — الكارت اللي المشرفة بتقرا منه الطالب قبل ما تستلمه.
 *
 * 🔴 **الخادم كان بيرجّعها والواجهة مابترسمهاش.** المقيّمة بتكتب مستوى
 *    الطالب وهواياته ونقاط ضعفه وتوصياتها، واللي هيستلم الطالب بعدها
 *    ما بيشوفش ولا حرف منها — نفس نمط «مكتوب وما اتعرضش» اللي ضربنا
 *    أربع مرات في أسبوع.
 */
function EvaluationCard({ student }: { student: StudentDetail }) {
  if (!student.evaluation_visible) {
    return (
      <Section title="نتيجة التقييم">
        {/* ⛔ «محجوب» ما ينفعش يتقرا «مفيش» — نطاق التقييم أضيق من نطاق
            الطلاب، فالحسابات بتشوف الطالب وما بتشوفش تقييمه. */}
        <HiddenSection what="نتيجة التقييم" />
      </Section>
    );
  }
  const e = student.evaluation;
  if (!e) {
    return (
      <Section title="نتيجة التقييم">
        <div className="t-desc">الطالب ده ما اتقيّمش لسه.</div>
      </Section>
    );
  }

  const text = (key: string): string | null => {
    const v = e[key];
    return typeof v === "string" && v.trim() ? v : null;
  };
  const level = text("student_level_label_ar");
  const rows: { label: string; value: string }[] = [
    { label: "هواياته واهتماماته", value: text("hobbies_ar") ?? "" },
    { label: "الحاجات اللي بيحبها", value: text("likes_ar") ?? "" },
    { label: "مستواه الدراسي الحالي", value: text("current_study_level_ar") ?? "" },
    { label: "درس إيه قبل كده", value: text("prior_study_ar") ?? "" },
    { label: "درس فين قبل كده", value: text("prior_study_place_ar") ?? "" },
    { label: "نقاط القوة", value: text("strengths_ar") ?? "" },
    { label: "محتاج يتطوّر في", value: text("weaknesses_ar") ?? "" },
    { label: "توصيات المقيّمة", value: text("recommendations_ar") ?? "" },
    { label: "ملاحظات", value: text("notes_ar") ?? "" },
  ].filter((r) => r.value);

  return (
    <Section title="نتيجة التقييم">
      <div className="deflist">
        {level ? (
          <DefRow k="مستواه من وجهة نظر المقيّمة">
            <span className="t-desc">{level}</span>
          </DefRow>
        ) : null}
        {rows.map((r) => (
          <DefRow key={r.label} k={r.label}>
            {/* السطور الجديدة بتتحفظ زي ما المقيّمة كتبتها */}
            <span className="t-desc" style={{ whiteSpace: "pre-line" }}>
              {r.value}
            </span>
          </DefRow>
        ))}
        {/* ⚠️ التقييم اللي فيه الروبرك وبس (من غير الاستمارة الموسّعة) لازم
            يقول كده — الكارت الفاضي بيتقرا «فيه عطل». */}
        {rows.length === 0 && !level ? (
          <div className="t-desc">
            اتسجّل تقييم للطالب ده، بس من غير بيانات وصفية.
          </div>
        ) : null}
      </div>
    </Section>
  );
}

function StudyTab({ student }: { student: StudentDetail }) {
  if (!student.enrollments_visible) {
    return (
      <>
        <EvaluationCard student={student} />
        <Section title="الاشتراكات">
          <HiddenSection what="الاشتراكات" />
        </Section>
      </>
    );
  }
  if (student.enrollments.length === 0) {
    return (
      <>
        <EvaluationCard student={student} />
        <Section title="الاشتراكات">
          <div className="t-desc">مفيش اشتراكات مسجّلة للطالب ده.</div>
        </Section>
      </>
    );
  }
  return (
    <>
      <EvaluationCard student={student} />
    <Section title="الاشتراكات" note="المعلم الحالي بيبان هنا — مش في قايمة الطلاب">
      <div className="reg-scroll" style={{ margin: "0 -20px" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>المادة</th>
              <th>المعلم</th>
              <th>الحالة</th>
              <th>بدأ</th>
              <th>بينتهي</th>
            </tr>
          </thead>
          <tbody>
            {student.enrollments.map((e) => (
              <tr key={e.id}>
                <td className="t-body">
                  <Maybe v={e.subject} />
                </td>
                <td className="t-desc">
                  {e.teacher ? e.teacher.name_ar : <span className="missing">مفيش معلم متسند</span>}
                </td>
                <td>
                  {e.status_label_ar ? (
                    <span className={`chip ${e.status === "active" ? "okc" : "ghost"}`}>
                      <span className="bullet" />
                      {latinDigits(e.status_label_ar)}
                    </span>
                  ) : (
                    <Dash />
                  )}
                </td>
                <td className="t-desc">
                  <DateAr v={e.started_at} />
                </td>
                <td className="t-desc">
                  <DateAr v={e.ends_at} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   (ج) التواصل والمشكلات
   ═══════════════════════════════════════════════════════════════════ */

/**
 * تبويب التواصل — **قراءة وكتابة**.
 *
 * 🔑 القيد بيتسجّل على **الأسرة** (قرار مقفول في الباك إند)، والموظفة بتسجّله
 *    وهي فاتحة سجل الطالب — فاللوحة بتبعت `family.id` ومعاه `student.id`
 *    كـ«ده يخص الولد ده بالذات».
 *
 * ⛔ اللوحة بتترسم في حالة **الفاضي** كمان مش في القايمة بس: الأسرة اللي
 *    مالهاش ولا قيد هي بالظبط اللي عايزين نسجّل ليها أول تواصل، ولو الزرار
 *    اتحط في فرع القايمة بس يبقى الطالب اللي «آخر تواصل» عنده «—» للأبد هو
 *    اللي مفيش عنده زرار.
 */
function ContactTab({
  student,
  canWrite,
  onSaved,
}: {
  student: StudentDetail;
  canWrite: boolean;
  onSaved: (toastAr: string) => void;
}) {
  if (!student.contact_log_visible) {
    return (
      <Section title="سجل التواصل مع ولي الأمر">
        <HiddenSection what="سجل التواصل" />
      </Section>
    );
  }

  const panel = !student.family ? (
    // ⚠️ الطالب المستورد ممكن يكون مش مربوط بأسرة — والقيد بيتعلّق بالأسرة،
    //    فمفيش مسار نبعت عليه. بنقول السبب بدل ما `student.family!.id` يقع.
    <Section title="تسجيل تواصل">
      <div className="t-desc">
        الطالب ده مش مربوط بأسرة — والتواصل بيتسجّل على الأسرة. اربطه بأسرة الأول.
      </div>
    </Section>
  ) : !canWrite ? (
    <Section title="تسجيل تواصل">
      <div className="t-desc">مالكش صلاحية تسجّل تواصل — العرض بس.</div>
    </Section>
  ) : (
    <LogContactPanel student={student} familyId={student.family.id} onSaved={onSaved} />
  );

  if (student.contact_log.length === 0) {
    return (
      <>
        {panel}
        <Section title="سجل التواصل مع ولي الأمر">
          {/* ⛔ مفيش تاريخ مخترَع هنا — «مفيش» معناها مفيش قيد أصلًا */}
          <div className="t-desc">مفيش تواصل مسجّل مع أسرة الطالب ده لحد دلوقتي.</div>
        </Section>
      </>
    );
  }

  return (
    <>
      {panel}
      <Section
        title="سجل التواصل مع ولي الأمر"
        note={
          <>
            <span className="num">{fmtNum(student.contact_log.length)}</span> قيد — الأحدث الأول
          </>
        }
      >
        {student.contact_log.map((c, i) => (
          <div
            key={c.id}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              borderTop: i === 0 ? undefined : "1px solid var(--line)",
              paddingTop: i === 0 ? undefined : 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span className="t-body">
                <DateAr v={c.at} />
              </span>
              {channelAr(c.channel) ? <span className="chip ghost">{channelAr(c.channel)}</span> : null}
              {/* 🔑 «إحنا كلّمناهم» ولا «هم كلّمونا» — الاتجاه متخزّن من زمان
                  وما كانش بيترسم، والقيدان بيبانوا زي بعض من غيره. */}
              {directionAr(c.direction) ? (
                <span className="chip ghost">{directionAr(c.direction)}</span>
              ) : null}
              <span className="t-meta">
                {c.by_user ? `تواصلت: ${c.by_user.name_ar}` : "مين تواصل: مش مسجّل"}
              </span>
              {/* من غير العلامة دي، الأخوان بيعرضوا نفس القيود بلا أي فرق —
                  فالخانة الاختيارية بتتكتب وما بتفرقش في حاجة. */}
              {/* ⛔ القيد اللي اتسجّل صراحةً على **أخ تاني** كان بيبان في
                  سجل الولد ده بلا أي فرق عن قيود الأسرة العامة — فالموظفة
                  تقرا «الولد مش بيحضر خالص» في سجل الولد الغلط. */}
              {c.student_id ? (
                c.student_id === student.id ? (
                  <span className="chip ghost">يخص {student.name_ar}</span>
                ) : (
                  <span className="t-meta">يخص أخًا تانيًا في الأسرة</span>
                )
              ) : null}
              {c.is_backdated ? (
                <span
                  className="t-meta"
                  title={c.logged_at ? `اتسجّل ${fmtDate(c.logged_at)}` : undefined}
                >
                  (اتسجّل متأخر)
                </span>
              ) : null}
              {!c.is_linked ? (
                // القيد ملقاش أسرته — بيفضل بالاسم الخام ومحتاج ربط بالإيد
                <span className="src" title={c.subject_name_raw ?? undefined}>
                  مش مربوط بأسرة
                </span>
              ) : null}
            </div>
            <div className="t-body">
              {c.summary_ar ? latinDigits(c.summary_ar) : <span className="missing">بلا ملخّص مكتوب</span>}
            </div>
            {c.parent_reply_ar ? (
              <div className="t-desc">رد ولي الأمر: «{latinDigits(c.parent_reply_ar)}»</div>
            ) : null}
          </div>
        ))}
      </Section>
    </>
  );
}

/**
 * لوحة تسجيل التواصل — ستايل سطري بنفس نمط لوحة الإقفال في `Leads.tsx`.
 *
 * ⛔ `busy` **مش تحسين**: ضغطتان متتاليتان = قيدان، ومفيش حذف ولا حذف ناعم
 *    لسجل التواصل — فالقيد المكرر بيفضل في السجل للأبد.
 * ⛔ والقفل على طول الملخّص قبل الإرسال بيمنع رحلة سيرفر بترجع خطأ نعرفه.
 */
function LogContactPanel({
  student,
  familyId,
  onSaved,
}: {
  student: StudentDetail;
  familyId: string;
  onSaved: (toastAr: string) => void;
}) {
  const [channel, setChannel] = useState<string>("call");
  const [direction, setDirection] = useState<"in" | "out">("out");
  const [summary, setSummary] = useState("");
  const [reply, setReply] = useState("");
  const [aboutThisChild, setAboutThisChild] = useState(true);
  // 🔑 فاضي = «دلوقتي». الموظفة بتملاه بس لما التواصل يكون حصل قبل كده.
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = summary.trim().length >= 3;

  async function save() {
    if (busy || !ready) return;
    setBusy(true);
    setError(null);
    try {
      const out = await logFamilyContact(familyId, {
        channel,
        direction,
        summaryAr: summary.trim(),
        parentReplyAr: reply,
        studentId: aboutThisChild ? student.id : null,
        occurredAt: when ? new Date(when).toISOString() : null,
      });
      setSummary("");
      setReply("");
      setWhen("");
      onSaved(out.toastAr);
    } catch (err) {
      console.error("[student] تسجيل التواصل فشل", err);
      setError(errorMessageAr(err));
    } finally {
      setBusy(false);
    }
  }

  const controlStyle = {
    font: "inherit",
    color: "var(--ink)",
    background: "var(--surface)",
    border: "1px solid var(--line)",
    borderRadius: "var(--r-control)",
    padding: "8px 10px",
    width: "100%",
    resize: "vertical",
  } as const;

  return (
    <Section title="تسجيل تواصل" note="القيد بيتسجّل على الأسرة — وبيبان لكل مشرفة بعدك">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 150 }}>
            <span className="t-meta">القناة</span>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              style={{ ...controlStyle, width: "auto" }}
            >
              {CONTACT_CHANNEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label_ar}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 170 }}>
            <span className="t-meta">الاتجاه</span>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value === "in" ? "in" : "out")}
              style={{ ...controlStyle, width: "auto" }}
            >
              <option value="out">منّا لولي الأمر</option>
              <option value="in">من ولي الأمر</option>
            </select>
          </label>
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">ملخّص التواصل</span>
          <textarea
            rows={2}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="الأسرة قالت إيه بالظبط"
            style={controlStyle}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">رد ولي الأمر (اختياري)</span>
          <textarea
            rows={2}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="لو رد بحاجة تستاهل تتسجّل"
            style={controlStyle}
          />
        </label>

        {/* ⛔ **مش حقل شكلي.** الموظفة بتعمل مكالماتها على الموبايل وبتدخّلها
            بعد يومين. لو الكل اتختم بوقت الإدخال، «آخر تواصل» بيقول «من ساعة»
            للكل وفرز «الأقدم تواصلًا» بينزّل الأسرة المنسية لآخر القايمة. */}
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="t-meta">التواصل حصل امتى؟ (سيبها فاضية لو دلوقتي)</span>
          <input
            type="datetime-local"
            className="field"
            value={when}
            max={new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
              .toISOString()
              .slice(0, 16)}
            disabled={busy}
            onChange={(e) => setWhen(e.target.value)}
            style={{ height: 36 }}
          />
        </label>

        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={aboutThisChild}
            onChange={(e) => setAboutThisChild(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            التواصل ده يخص {student.name_ar} بالذات
            <span className="t-meta">
              {" "}
              — القيد بيتسجّل على الأسرة في كل الأحوال، والاختيار ده بيربطه بالولد ده كمان
            </span>
          </span>
        </label>

        {error ? (
          <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
            {error}
          </span>
        ) : null}

        <div className="fix-line">
          <span className="t-meta">
            {ready ? "جاهز للتسجيل" : "اكتب ملخّص التواصل الأول"}
          </span>
          {/* ⛔ فعل أساسي واحد في اللوحة */}
          <button
            type="button"
            className={`btn sm ${ready ? "primary" : "is-off"}${busy ? " is-busy" : ""}`}
            style={{ marginRight: "auto" }}
            disabled={busy || !ready}
            aria-busy={busy}
            onClick={() => void save()}
          >
            {busy ? "بيتسجّل…" : "سجّل التواصل"}
          </button>
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   (د) الحضور والإجازات — design/StudentLeaves.dc.html
   ═══════════════════════════════════════════════════════════════════ */

function LeavesTab({ student }: { student: StudentDetail }) {
  return (
    <>
      {/* تحذير المصدر زي التصميم بالحرف */}
      <div
        className="card flat"
        style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}
      >
        <span className="t-body">
          البورتال الأساسي ما بيسجّلش إجازات — بنسجّلها هنا، وبتظهر للمعلم والإشراف تلقائيًا.
        </span>
      </div>

      <div className="sec-h">
        <span className="sec-title">الإجازات والتوقف</span>
        <span className="t-meta" style={{ marginRight: "auto" }}>
          تسجيل إجازة جديدة لسه بيتجهّز
        </span>
      </div>

      {!student.leaves_visible ? (
        <div className="card" style={{ padding: 20 }}>
          <HiddenSection what="الإجازات" />
        </div>
      ) : student.leaves.length === 0 ? (
        <div className="card" style={{ padding: 20 }}>
          <div className="t-desc">مفيش إجازات ولا توقف مسجّل للطالب ده.</div>
        </div>
      ) : (
        <div
          className="card"
          style={{ padding: "18px 20px", display: "flex", flexDirection: "column" }}
        >
          {student.leaves.map((lv) => {
            const src = leaveSource(lv.report_source);
            const policy = hoursPolicyAr(lv.hours_policy);
            return (
              <div className="lv" key={lv.id}>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {lv.status_label_ar ? (
                    <span className={`chip ${leaveTone(lv.status)}`}>
                      <span className="bullet" />
                      {latinDigits(lv.status_label_ar)}
                    </span>
                  ) : (
                    <Dash />
                  )}
                  <span className="t-meta">
                    {/* ⚠️ فترة فيها اسم شهر عربي — <bdi> بلا dir */}
                    <DateRangeAr
                      from={lv.start_date}
                      to={lv.end_date}
                      endUnknown={lv.end_date_unknown}
                    />
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                  <span className="t-body">
                    {lv.type_label_ar ? latinDigits(lv.type_label_ar) : <Dash />}
                    {lv.reason_ar ? ` — ${latinDigits(lv.reason_ar)}` : ""}
                    {!lv.reason_ar ? (
                      // ⛔ ممنوع «غير مذكور» — النقص بيتقال بوصفه نقص
                      <span className="missing"> — السبب مش مكتوب في السجل</span>
                    ) : null}
                  </span>
                  <span
                    className="t-meta"
                    style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}
                  >
                    {policy ? <span>{policy}</span> : null}
                    {src ? (
                      <span className={`srcbadge${src.automatic ? "" : " man"}`}>{src.label_ar}</span>
                    ) : (
                      <span className="missing">المصدر مش مسجّل</span>
                    )}
                    <NeedsReviewMark q={lv.data_quality} compact />
                  </span>
                  {lv.expected_return_at ? (
                    <span className="t-meta">
                      الرجوع المتوقّع: <DateAr v={lv.expected_return_at} />
                    </span>
                  ) : null}
                  {lv.end_date_unknown && lv.next_check_at ? (
                    <span className="t-meta">
                      متابعة جاية: <DateAr v={lv.next_check_at} />
                    </span>
                  ) : null}
                </div>
                {/* العمود التالت في التصميم فعل («تعديل»/«فتح») — تعديل
                    الإجازات لسه ما اتبناش، فالخانة بتفضل فاضية بدل زرار
                    بيضغط وما بيعملش حاجة. */}
                {lv.status === "active" ? (
                  <span className="t-meta">شغّالة دلوقتي</span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   (هـ) الحسابات — الساعات نص، ⛔ ممنوع الفلوت
   ═══════════════════════════════════════════════════════════════════ */

function AccountsTab({ student }: { student: StudentDetail }) {
  if (!student.enrollments_visible) {
    return (
      <Section title="ساعات الكورس">
        <HiddenSection what="الاشتراكات والساعات" />
      </Section>
    );
  }
  if (student.enrollments.length === 0) {
    return (
      <Section title="ساعات الكورس">
        <div className="t-desc">مفيش اشتراكات، يبقى مفيش رصيد ساعات.</div>
      </Section>
    );
  }
  return (
    <Section
      title="ساعات الكورس"
      note="الأرقام نص زي ما رجعت من السيستم — ⛔ بلا تقريب"
    >
      <div className="reg-scroll" style={{ margin: "0 -20px" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>المادة</th>
              <th>متعاقد عليها</th>
              <th>اتستهلكت</th>
              <th>المتبقي</th>
              <th>رقم البورتال</th>
              <th>الفرق</th>
            </tr>
          </thead>
          <tbody>
            {student.enrollments.map((e) => (
              <tr key={e.id}>
                <td className="t-body">
                  <Maybe v={e.subject} />
                </td>
                <td>
                  <Hours v={e.contracted_hours} />
                </td>
                <td>
                  <Hours v={e.hours_consumed} />
                </td>
                <td>
                  <Hours v={e.hours_balance} />
                </td>
                <td>
                  <Hours v={e.portal_hours_reported} />
                </td>
                <td>
                  {isNonZero(e.hours_variance) ? (
                    // ⚠️ الفرق بيتعرض كتحذير وما بيتصلّحش أوتوماتيك أبدًا
                    <span className="chip soon">
                      <span className="bullet" />
                      <Ltr>{e.hours_variance ?? ""}</Ltr>
                    </span>
                  ) : e.hours_variance !== null ? (
                    <Hours v={e.hours_variance} />
                  ) : (
                    <Dash />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/** ساعة معروضة زي ما رجعت — نص، جوّه `<Ltr>` لأنه رقم لاتيني صافي */
function Hours({ v }: { v: string | null }) {
  if (v === null || v === "") return <Dash />;
  return (
    <span className="t-body">
      <Ltr>{v}</Ltr> ساعة
    </span>
  );
}

function isNonZero(v: string | null): boolean {
  if (!v) return false;
  const n = Number(v);
  return Number.isFinite(n) ? n !== 0 : v.trim() !== "0";
}

/* ═══════════════════════════════════════════════════════════════════
   (و) الإدارة — من فين جه السجل ومين راجعه
   ═══════════════════════════════════════════════════════════════════ */

function AdminTab({
  student,
  family,
  canChange,
  canElevate,
  canTagFamily,
  onOpenLeaves,
  onSaved,
}: {
  student: StudentDetail;
  family: FamilyDetail | null;
  canChange: boolean;
  canElevate: boolean;
  canTagFamily: boolean;
  onOpenLeaves: () => void;
  onSaved: (toastAr: string) => void;
}) {
  return (
    <>
      {/* الفعل جنب أثره: «آخر تغيير حالة» و«نسخة السجل» في نفس التبويب */}
      <StatusCard
        student={student}
        canChange={canChange}
        canElevate={canElevate}
        onOpenLeaves={onOpenLeaves}
        onSaved={onSaved}
      />

      <Section title="مصدر السجل والمراجعة">
        <div className="deflist">
          <DefRow k="نسخة السجل">
            <span className="num">{fmtNum(student.version)}</span>
          </DefRow>
          <DefRow k="المسؤولة">
            {student.owner ? student.owner.name_ar : <Dash />}
          </DefRow>
          <DefRow k="آخر تغيير حالة">
            <DateAr v={student.status_changed_at} />
          </DefRow>
          <DefRow k="شيت الاستيراد">
            {/* ⛔ **مش <Ltr>**: اسم التبويبة عربي دايمًا («التوقف الدائم(
                الفرنسيين)»)، و<Ltr> بيرندر <bdi dir="ltr"> اللي بيقلب ترتيب
                المقاطع لما المحتوى فيه عربي. الفخ ده ضرب ٥ مرات في المشروع.
                <bdi> بلا dir بيعزل النص من غير ما يفرض اتجاهًا. */}
            {student.data_quality.source?.sheet ? (
              <bdi>{student.data_quality.source.sheet}</bdi>
            ) : (
              <Dash />
            )}
          </DefRow>
          <DefRow k="صف الشيت">
            {student.data_quality.source?.row === null ||
            student.data_quality.source?.row === undefined ? (
              <Dash />
            ) : (
              <span className="num">{fmtNum(student.data_quality.source.row)}</span>
            )}
          </DefRow>
          <DefRow k="اتراجع في">
            <DateAr v={student.data_quality.reviewed_at} />
          </DefRow>
        </div>
      </Section>

      {student.data_quality.raw && Object.keys(student.data_quality.raw).length > 0 ? (
        <Section title="النص الخام من الشيت" note="بيبان لمين معاه صلاحية التصليح بس">
          <div className="deflist">
            {Object.entries(student.data_quality.raw).map(([k, v]) => (
              <DefRow k={k} key={k}>
                <span className="raw-quote">{v}</span>
              </DefRow>
            ))}
          </div>
        </Section>
      ) : null}

      {family ? (
        <FamilyTagCard family={family} canWrite={canTagFamily} onSaved={onSaved} />
      ) : null}

      {family ? <QualityCard title="جودة سجل الأسرة" q={family.data_quality} /> : null}
    </>
  );
}

/**
 * 🔴 تصنيف الأسرة — المسار الوحيد اللي الفريق يقدر يصحّح بيه قايمة VIP
 * وهو شغّال. نفس فلسفة الشاشة: «الاستكمال في مكانه».
 *
 * ⚠️ **نسخة الأسرة مش نسخة الطالب.** الشاشة ماسكة الاتنين مع بعض، والمسار
 *    `/families/{id}/tags` بيقارن نسخة **الأسرة** — إرسال نسخة الطالب
 *    بيرجّع 409 «حد سبقك»، والموظفة بتعيد التحميل وتلاقي كل حاجة سليمة
 *    وتحاول تاني وتفشل تاني.
 */
function FamilyTagCard({
  family,
  canWrite,
  onSaved,
}: {
  family: FamilyDetail;
  canWrite: boolean;
  onSaved: (toastAr: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const isVip = family.is_vip;
  const ready = reason.trim().length >= 3;

  async function apply(action: "add" | "remove") {
    if (busy || !ready) return;
    setBusy(true);
    setError(null);
    setStale(false);
    try {
      const out = await setFamilyTag(family.id, family.version, {
        tag: "vip",
        action,
        reason_ar: reason.trim(),
      });
      setReason("");
      onSaved(out.toastAr);
    } catch (err) {
      console.error("[family] تغيير تصنيف الأسرة فشل", err);
      setError(errorMessageAr(err));
      setStale(err instanceof ApiError && err.status === 409);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="تصنيف الأسرة">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {family.tags_ar.length > 0 ? (
            <>
              <VipMark tagsAr={family.tags_ar} />
              {family.tags_ar
                .filter((t) => t.key === "referral")
                .map((t) => (
                  <span key={t.key} className="chip ghost">
                    {t.label_ar}
                  </span>
                ))}
            </>
          ) : (
            <span className="missing">الأسرة دي مالهاش تصنيف</span>
          )}
        </div>

        {!canWrite ? (
          <div className="t-desc">مالكش صلاحية تغيّر التصنيف — العرض بس.</div>
        ) : (
          <>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="t-meta">
                السبب — «مين حطّ الأسرة دي في VIP وليه» سؤال بيتسأل
              </span>
              <textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="مثال: الإدارة طلبت رفعها بعد مكالمة ولي الأمر"
                style={{
                  font: "inherit",
                  color: "var(--ink)",
                  background: "var(--surface)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-control)",
                  padding: "8px 10px",
                  width: "100%",
                  resize: "vertical",
                }}
              />
            </label>

            {error ? (
              <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
                {error}
                {stale ? " — اضغط «تحديث السجل» فوق وشوف التصنيف الجديد الأول." : ""}
              </span>
            ) : null}

            <div className="fix-line">
              <span className="t-meta">
                {ready ? "جاهز" : "اكتب السبب — بلا سبب مفيش تغيير"}
              </span>
              <button
                type="button"
                className={`btn sm ${ready ? "primary" : "is-off"}${busy ? " is-busy" : ""}`}
                style={{ marginRight: "auto" }}
                disabled={busy || !ready}
                aria-busy={busy}
                onClick={() => void apply(isVip ? "remove" : "add")}
              >
                {busy ? "بيتغيّر…" : isVip ? "شيل VIP" : "حطّ VIP"}
              </button>
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

/**
 * 🔴 كارت تغيير الحالة — قرار إداري **بسبب مكتوب**، مش استكمال داتا.
 *
 * ⚠️ الحالة و«التوقفات» بيسجّلوا نفس الواقعة في مكانين، ومفيش سطر واحد في
 *    السيستم بيربطهم (`update_leave` ما بيلمسش `students.status`). لحد ما
 *    القرار ده يتاخد، الشاشة **بتقول ده صراحةً** لما يفترقوا بدل ما تسيب
 *    الموظفة تفتكر إنها قفلت التوقف.
 */
function StatusCard({
  student,
  canChange,
  canElevate,
  onOpenLeaves,
  onSaved,
}: {
  student: StudentDetail;
  canChange: boolean;
  canElevate: boolean;
  onOpenLeaves: () => void;
  onSaved: (toastAr: string) => void;
}) {
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const options = MANUAL_STUDENT_STATUSES.filter(
    (o) => o.value !== student.status && (canElevate || !o.elevated),
  );
  const ready = status !== "" && reason.trim().length >= 3;
  /** الحالة الحالية لو كانت **مش يدوية** — بنقول ليه بدل ما تختفي بصمت */
  const notManualAr = student.status ? STATUS_NOT_MANUAL_AR[student.status] : undefined;

  // توقف لسه مفتوح — وتغيير الحالة **ما بيقفلوش**
  const openLeave =
    student.leaves_visible &&
    student.leaves.some((l) => l.status === "pending" || l.status === "active");

  async function save() {
    if (busy || !ready) return;
    setBusy(true);
    setError(null);
    setStale(false);
    try {
      const out = await setStudentStatus(student.id, student.version, {
        status,
        reason_ar: reason.trim(),
      });
      setStatus("");
      setReason("");
      onSaved(out.toastAr);
    } catch (err) {
      console.error("[student] تغيير حالة الطالب فشل", err);
      setError(errorMessageAr(err));
      // 409 = حد تاني عدّل السجل (أو المحرّك رفع نسخته) — القراءة تاني
      // إجبارية عشان نشوف الحالة الجديدة قبل ما نكتب فوقها.
      setStale(err instanceof ApiError && err.status === 409);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title="حالة الطالب">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {student.status_label_ar ? (
            <span className={`chip ${studentTone(student.status)}`}>
              <span className="bullet" />
              {latinDigits(student.status_label_ar)}
            </span>
          ) : (
            <Dash />
          )}
          <span className="t-meta">
            آخر تغيير: <DateAr v={student.status_changed_at} />
          </span>
        </div>
        <div className="t-body">
          {student.status_reason_ar ? (
            latinDigits(student.status_reason_ar)
          ) : (
            <span className="missing">مفيش سبب متسجّل للحالة دي</span>
          )}
        </div>

        {notManualAr ? <div className="t-desc">{notManualAr}</div> : null}

        {openLeave ? (
          // 🔴 الحارس الأهم بصريًا: السجلان بيفترقوا وأول ضحية هي الموظفة
          <div className="t-desc" role="note">
            السجل ده فيه توقف لسه مفتوح — وتغيير الحالة من هنا <strong>ما بيقفلوش</strong>.{" "}
            <button type="button" className="link" onClick={onOpenLeaves}>
              افتح «الحضور والإجازات»
            </button>
          </div>
        ) : null}

        {!canChange ? (
          <div className="t-desc">مالكش صلاحية تغيّر الحالة — العرض بس.</div>
        ) : (
          <>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="t-meta">الحالة الجديدة</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{
                  font: "inherit",
                  color: "var(--ink)",
                  background: "var(--surface)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-control)",
                  padding: "8px 10px",
                  width: "auto",
                }}
              >
                <option value="">— اختار الحالة —</option>
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label_ar}
                  </option>
                ))}
              </select>
            </label>
            {!canElevate ? (
              <span className="t-meta">
                «إيقاف مالي» و«منسحب» قرار إدارة — مش في القايمة دي.
              </span>
            ) : null}

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="t-meta">
                السبب — بيتخزّن في السجل وبيتقرا بعد شهور
              </span>
              <textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="مثال: ولي الأمر طلب توقف شهر للسفر"
                style={{
                  font: "inherit",
                  color: "var(--ink)",
                  background: "var(--surface)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-control)",
                  padding: "8px 10px",
                  width: "100%",
                  resize: "vertical",
                }}
              />
            </label>

            {error ? (
              <span className="t-meta" role="alert" style={{ color: "var(--danger)" }}>
                {error}
                {stale ? " — اضغط «تحديث السجل» فوق وشوف الحالة الجديدة الأول." : ""}
              </span>
            ) : null}

            <div className="fix-line">
              <span className="t-meta">
                {status === ""
                  ? "اختار الحالة الجديدة"
                  : reason.trim().length < 3
                    ? "اكتب السبب — بلا سبب مفيش تغيير"
                    : "جاهز للتغيير"}
              </span>
              <button
                type="button"
                className={`btn sm ${ready ? "primary" : "is-off"}${busy ? " is-busy" : ""}`}
                style={{ marginRight: "auto" }}
                disabled={busy || !ready}
                aria-busy={busy}
                onClick={() => void save()}
              >
                {busy ? "بيتغيّر…" : "تغيير الحالة"}
              </button>
            </div>
          </>
        )}
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   بطاقات العمود الجانبي
   ═══════════════════════════════════════════════════════════════════ */

function FamilyCard({ state, siblings }: { state: FamilyState; siblings: SiblingRef[] }) {
  if (state.kind === "none") {
    return (
      <Section title="الأسرة">
        <div className="t-desc">الطالب ده مش مربوط بأسرة في السجل.</div>
      </Section>
    );
  }
  if (state.kind === "loading") return <LoadingState rows={2} />;
  if (state.kind === "blocked") {
    return (
      <Section title="الأسرة">
        <div className="t-desc">بيانات الأسرة مش متاحة لك — {state.messageAr}</div>
      </Section>
    );
  }
  const f = state.family;
  return (
    <Section
      title="الأسرة"
      action={
        <>
          <VipMark tagsAr={f.tags_ar} />
          <NeedsReviewMark q={f.data_quality} />
        </>
      }
    >
      <div className="deflist">
        <DefRow k="الأسرة">
          <Maybe v={f.name_ar} />
        </DefRow>
        <DefRow k="ولي الأمر">
          <Maybe v={f.guardian.name_ar} />
        </DefRow>
        <DefRow k="الرقم">
          {!f.guardian.phone_visible ? (
            <span className="missing">محجوب عن قسمك</span>
          ) : f.guardian.phone_masked ? (
            <Ltr>{f.guardian.phone_masked}</Ltr>
          ) : (
            <span className="missing">مفيش رقم مسجّل</span>
          )}
        </DefRow>
        <DefRow k="عدد الأولاد">
          {f.children_count === null ? <Dash /> : <span className="num">{fmtNum(f.children_count)}</span>}
        </DefRow>
        {/* 🔴 **الرقم لوحده كان بيلخبط.** مشرفة فتحت سجل بنت، شافت «عدد
            الأولاد ٢»، ومالقتش أختها في الشاشة — فبلّغت إن الداتا ناقصة.
            الأخت كانت موجودة ومربوطة بنفس التليفون من أول يوم. الشاشة بقت
            **تسمّي** مش تعدّ، والاسم رابط يفتح سجله. */}
        {siblings.length > 0 ? (
          <DefRow k="إخوته">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {siblings.map((s) => (
                <Link key={s.id} to={`/students/${s.id}`} className="chip ghost">
                  {s.name_ar}
                  {s.status_label_ar ? (
                    <span className="t-meta"> · {s.status_label_ar}</span>
                  ) : null}
                </Link>
              ))}
            </div>
          </DefRow>
        ) : null}
        <DefRow k="آخر تواصل">
          <DateAr v={f.last_contact_at} />
        </DefRow>
      </div>
    </Section>
  );
}

function QualityCard({ title, q }: { title: string; q: DataQuality }) {
  if (!q.needs_review) {
    return (
      <Section title={title}>
        <span className="chip okc">
          <span className="bullet" />
          مكتمل
        </span>
      </Section>
    );
  }
  return (
    <Section title={title} action={<NeedsReviewMark q={q} />}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {q.issues_ar.length === 0 ? (
          <span className="t-desc">السجل موسوم للمراجعة والسبب مش مكتوب.</span>
        ) : (
          q.issues_ar.map((issue, i) => (
            <span className="t-desc" key={`${issue}-${i}`}>
              · {issue}
            </span>
          ))
        )}
      </div>
    </Section>
  );
}

/**
 * رصيد الكورس بالساعات (design/StudentLeaves.dc.html).
 * ⚠️ الأرقام بتتعرض **نص** زي ما رجعت. شريط التقدم بيتحسب بالفلوت للعرض
 *    بس — ولو الرقم مش مقروء الشريط ما بيظهرش أصلًا بدل ما يعرض كدب.
 */
function CourseBalanceCard({
  enrollments,
  visible,
}: {
  enrollments: EnrollmentRow[];
  visible: boolean;
}) {
  if (!visible) {
    return (
      <Section title="رصيد الكورس">
        <HiddenSection what="الاشتراكات والساعات" />
      </Section>
    );
  }
  const active = enrollments.find((e) => e.status === "active") ?? enrollments[0] ?? null;
  if (!active) {
    return (
      <Section title="رصيد الكورس">
        <div className="t-desc">مفيش اشتراك، يبقى مفيش رصيد.</div>
      </Section>
    );
  }
  const balance = Number(active.hours_balance);
  const contracted = Number(active.contracted_hours);
  const pct =
    Number.isFinite(balance) && Number.isFinite(contracted) && contracted > 0
      ? Math.max(0, Math.min(100, Math.round((balance / contracted) * 100)))
      : null;

  return (
    <Section title="رصيد الكورس" note={active.subject ?? undefined}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        {active.hours_balance ? (
          <span style={{ fontSize: 26, fontWeight: 700, color: "var(--blue-900)" }} className="num">
            {active.hours_balance}
          </span>
        ) : (
          <span className="missing">الرصيد مش مسجّل</span>
        )}
        <span className="t-desc">
          {active.contracted_hours ? (
            <>
              ساعة من <Ltr>{active.contracted_hours}</Ltr> · بالساعات مش بالشهور
            </>
          ) : (
            "بالساعات مش بالشهور"
          )}
        </span>
      </div>
      {pct === null ? (
        <div className="t-meta">شريط التقدم مش معروض — الرصيد أو المتعاقد عليه مش رقم مقروء.</div>
      ) : (
        <div className="prog">
          <i style={{ width: `${pct}%` }} />
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          marginTop: 4,
          borderTop: "1px solid var(--line)",
          paddingTop: 14,
        }}
      >
        <DefRow k="اتستهلكت">
          <Hours v={active.hours_consumed} />
        </DefRow>
        <DefRow k="رقم البورتال">
          <Hours v={active.portal_hours_reported} />
        </DefRow>
        <DefRow k="الفرق">
          {isNonZero(active.hours_variance) ? (
            <span className="chip soon">
              <span className="bullet" />
              <Ltr>{active.hours_variance ?? ""}</Ltr>
            </span>
          ) : (
            <Hours v={active.hours_variance} />
          )}
        </DefRow>
      </div>
      <div className="t-meta">الإجازة ما بتمدّش الباقة.</div>
    </Section>
  );
}
